import { Check, DeleteOutline, InfoOutlined } from "@mui/icons-material";
import {
  Box,
  Stack,
  TextField,
  MenuItem,
  Button,
  Card,
  CardMedia,
  IconButton,
  Typography,
} from "@mui/material";
import { useState, useEffect } from "react";
import imageBlockService from "../../api/services/imageBlock.service";
import UploadImages from "../../components/UploadImages";
import { randomBlocks } from "../../utilities/constants";
import { Theme, useTheme } from "@mui/material/styles";
import { imgUrl } from "../../utilities/helpers";
import LoaderBigger from "../../components/LoaderBigger";
import { Droppable, Draggable, DragDropContext } from "react-beautiful-dnd";
import Loader from "../../components/Loader";
import ColorPicker from "../Projects/ImageBlocks/ColorPicker";
import randomService from "../../api/services/random.service";
import RandomBlockInfo from "./RandomBlockInfo";

const FormRandomBlock = ({
  selectedBlock,
  setSelectedBlock,
  refreshBlocks,
  setError,
  setSuccess,
  rank,
}) => {
  const theme: Theme = useTheme();
  const [images, setImages] = useState([]);
  const [selectedImages, setSelectedImages] = useState(null);
  const [previews, setPreviews] = useState([]);
  const [totalSize, setTotalSize] = useState<number>(0);

  const [imageCount, setImageCount] = useState<number>(1);
  const [identifier, setIdentifier] = useState<number>(1);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submittingRanks, setSubmittingRanks] = useState(false);

  const [colors, setColors] = useState<any>({});
  const [selectedImage, setSelectedImage] = useState<any>(null);
  const [openColorPicker, setOpenColorPicker] = useState<boolean>(false);
  const [showBlockInfo, setShowBlockInfo] = useState<string>("");

  useEffect(() => {
    if (selectedBlock && selectedBlock.images) {
      const id = selectedBlock.identifier;
      const { count } = randomBlocks.find((block) => block.value === id);
      setImageCount(count);
      setIdentifier(id);
      setSelectedImages(selectedBlock.images);
    }
  }, [selectedBlock, colors]);

  const handleBlockChange = (id: any) => {
    const uploaded =
      selectedBlock && selectedImages ? selectedImages.length : 0;
    const { count } = randomBlocks.find((block) => block.value === id);
    if (count === 0) {
      setImages((old) => [...old]);
      setPreviews((old) => [...old]);
    } else if (uploaded === 0) {
      setImages((old) => [...old].slice(-count));
      setPreviews((old) => [...old].slice(-count));
    } else if (uploaded < count) {
      setImages((old) => [...old].slice(-(count - uploaded)));
      setPreviews((old) => [...old].slice(-(count - uploaded)));
    } else if (uploaded > count) {
      setImages((old) => [...old].slice(0, count));
      setPreviews((old) => [...old].slice(0, count));
    } else {
      setImages([]);
      setPreviews([]);
    }

    setIdentifier(id);
    setImageCount(count);
  };

  const handleResetForm = () => {
    setError("");
    setSuccess("");
    setImages([]);
    setPreviews([]);
    setIdentifier(1);
    setImageCount(1);
    setTotalSize(0);
    setSelectedBlock(null);
    setSelectedImages(null);
  };

  function handleCreateImages(block: string) {
    setSubmitting(true);
    if (images.length === 0) {
      setSubmitting(false);
      return;
    }
    const formData = new FormData();
    images.forEach((image) => {
      formData.append("files", image);
    });

    const _colors = previews.map((image) => image.color);

    formData.append("colors", JSON.stringify(_colors));

    randomService
      .createRandomBlocksImages(formData, block)
      .then((res) => {
        if (res.data.success) {
          setImages([]);
          setPreviews([]);
          setSubmitting(false);
          hijackRefreshBlocks();
        } else {
          setError(res.data.error?.message);
        }
      })
      .catch((error) => {
        setSubmitting(false);
        setError(error.message);
      });
  }

  function handleUpdateImageColor(id: string) {
    setError("");
    const color = colors[id];

    if (!color) return;

    imageBlockService
      .updateImage({ color }, id)
      .then((res) => {
        if (res.data.success) {
          const _colors = Object.assign({}, colors);
          delete _colors[id];
          setColors(_colors);
        } else {
          setError(res.data.error?.message);
        }
      })
      .catch((error) => {
        setError(error.message);
      });
  }

  const handleCloseColorPicker = () => {
    setOpenColorPicker(false);
    setSelectedImage(null);
  };

  const handleOpenColorPicker = (image: any) => {
    setSelectedImage(image);
    setOpenColorPicker(true);
  };

  const hijackRefreshBlocks = () => {
    // handleResetForm();
    refreshBlocks();
  };

  const handleSubmitBlock = async () => {
    setSubmitting(true);
    setError("");
    setSuccess("");
    if (selectedBlock) {
      randomService
        .updateRandomBlock({ identifier }, selectedBlock._id)
        .then((res) => {
          if (res.data.success) {
            handleCreateImages(res.data.resource._id);
            setSuccess("Image block updated");
            hijackRefreshBlocks();
          } else {
            setSubmitting(false);
            setError(res.data.error?.message);
          }
        })
        .catch((error) => {
          setSubmitting(false);
          setError(error.message);
        });
    } else {
      randomService
        .createRandomBlock({ identifier, rank })
        .then((res) => {
          if (res.data.success) {
            setSelectedBlock(res.data.resource);
            handleCreateImages(res.data.resource._id);
            setSuccess("Image block created");
            hijackRefreshBlocks();
          } else {
            setSubmitting(false);
            setError(res.data.error?.message);
          }
        })
        .catch((error) => {
          setSubmitting(false);
          setError(error.message);
        });
    }
  };

  const handleDeleteImage = (image: any) => {
    setSubmitting(true);
    imageBlockService
      .deleteImage(image._id)
      .then((res) => {
        if (res.data.success) {
          setSubmitting(false);
          hijackRefreshBlocks();
        } else {
          setSubmitting(false);
          setError(res.data.error?.message);
        }
      })
      .catch((error) => {
        setSubmitting(false);
        setError(error.message);
      });
  };

  const handleOnDragEndUploaded = (result: any) => {
    if (!result.destination) return;
    const items = Array.from(selectedImages);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setSelectedImages(items);

    const ids = items.map((item: any) => item._id);
    handleUpdateRank(ids);
  };

  const handleUpdateRank = (items: any) => {
    setSubmittingRanks(true);
    imageBlockService
      .updateImageRank(items)
      .then((res) => {
        if (res.data.success) {
          // Success
        } else {
          console.log(res.data.error?.message);
        }
        setSubmittingRanks(false);
      })
      .catch((error) => {
        setSubmittingRanks(false);
        console.log("Error Block Ranks:", error);
      });
  };

  return (
    <>
      {submitting && <LoaderBigger />}
      {submittingRanks && <Loader />}
      <Stack
        direction="column"
        justifyContent="flex-start"
        alignItems="flex-start"
        spacing={2}
        sx={{ width: "100%" }}
      >
        <Stack
          direction="row"
          justifyContent="flex-start"
          alignItems="flex-start"
          spacing={2}
          sx={{ width: { xs: "100%", sm: "60%" } }}
        >
          <TextField
            fullWidth
            select
            label="Image Block"
            name="block"
            size="small"
            value={identifier}
            onChange={(e) => handleBlockChange(e.target.value)}
            helperText={`Max ${imageCount ? imageCount : "infinite"} image(s)`}
            sx={{
              width: "20%",
              "& .MuiFormHelperText-filled": {
                marginLeft: 0,
                marginRight: 0,
                fontSize: "11px",
                color: "#909090",
              },
            }}
          >
            {randomBlocks.map((block) => (
              <MenuItem key={block.value} value={block.value}>
                {block.value}
              </MenuItem>
            ))}
          </TextField>

          <Button
            onClick={handleSubmitBlock}
            disabled={totalSize > 20_000_000}
            sx={{
              p: "8px",
              width: "20%",
              backgroundColor: `${totalSize > 20_000_000 ? "red" : "#43a310"}`,
            }}
          >
            {selectedBlock ? "Update" : "Create"}
          </Button>

          <Button onClick={handleResetForm} sx={{ p: "8px" }}>
            Reset Form
          </Button>

          <Box>
            <IconButton
              onClick={() => setShowBlockInfo("yes")}
              onMouseEnter={() => setShowBlockInfo("yes")}
            >
              <InfoOutlined />
            </IconButton>
            {showBlockInfo && (
              <RandomBlockInfo
                identifier={identifier}
                setShowBlockInfo={setShowBlockInfo}
              />
            )}
          </Box>
        </Stack>

        <Box sx={{ width: "100%" }}>
          <UploadImages
            totalSize={totalSize}
            setTotalSize={setTotalSize}
            inputBoxSx={{ width: { xs: "100%", sm: "60%" } }}
            imageHeight="120px"
            multiple
            max={imageCount}
            required
            handleBlockChange={handleBlockChange}
            identifier={identifier}
            files={images}
            setFiles={setImages}
            previews={previews}
            setPreviews={setPreviews}
          />
        </Box>

        {selectedImages && (
          <>
            <Box
              sx={{
                backgroundColor: "#f3f2f2",
                height: "1px",
                width: "100%",
                m: "8px 0",
              }}
            />

            <DragDropContext onDragEnd={handleOnDragEndUploaded}>
              <Droppable droppableId="uploaded-images" direction="horizontal">
                {(provided) => (
                  <Box
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    sx={{ mt: "10px", width: "100%" }}
                  >
                    {selectedImages.map((image, index) => (
                      <Draggable
                        key={image._id}
                        draggableId={image._id}
                        index={index}
                      >
                        {(provided) => (
                          <Box
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            sx={{
                              display: "inline-block",
                              width: "fit-content",
                            }}
                          >
                            <Stack
                              direction="column"
                              justifyContent="flex-start"
                              alignItems="flex-start"
                            >
                              <Card
                                id={image._id}
                                elevation={0}
                                sx={{
                                  padding: "0",
                                  margin: "4px",
                                  borderRadius: "2px",
                                  position: "relative",
                                  width: "fit-content",
                                  maxHeight: "120px",
                                }}
                              >
                                {!image.vimeo && (
                                  <CardMedia
                                    sx={{
                                      height: "120px",
                                      width: "fit-content",
                                      display: "inline-block",
                                    }}
                                    component="img"
                                    src={imgUrl(image.slug)}
                                    alt={image.alt}
                                  />
                                )}

                                <IconButton
                                  onClick={() => handleDeleteImage(image)}
                                  sx={{
                                    color: theme.colors.error,
                                    position: "absolute",
                                    top: "0",
                                    right: "0",
                                  }}
                                >
                                  <DeleteOutline />
                                </IconButton>
                              </Card>

                              <Stack
                                direction="row"
                                justifyContent="flex-start"
                                alignItems="center"
                                spacing={0.8}
                                sx={{ m: "4px" }}
                              >
                                <Box
                                  onClick={() => handleOpenColorPicker(image)}
                                  sx={{
                                    backgroundColor:
                                      colors[image._id] || image.color,
                                    height: "28px",
                                    width: "28px",
                                    cursor: "pointer",
                                    borderRadius: "4px",
                                    border: "1px solid #e6e6e6",
                                  }}
                                />
                                <Typography>{image.color}</Typography>
                                {colors[image._id] && (
                                  <Button
                                    onClick={() =>
                                      handleUpdateImageColor(image._id)
                                    }
                                    sx={{ minWidth: "auto" }}
                                  >
                                    <Check
                                      sx={{ height: "16px", width: "16px" }}
                                    />
                                  </Button>
                                )}
                              </Stack>
                            </Stack>
                          </Box>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
          </>
        )}
      </Stack>

      {selectedImage && (
        <ColorPicker
          colors={colors}
          setColors={setColors}
          image={selectedImage}
          open={openColorPicker}
          handleClose={handleCloseColorPicker}
        />
      )}
    </>
  );
};

export default FormRandomBlock;
